import { Box, CloseButton, Group, Paper, Stack, Text, TextInput, Transition, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { forwardRef, ReactNode, useEffect, useState } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface SpotlightAction {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  group?: string;
  onClick: () => void;
}

export interface SpotlightProps {
  opened: boolean;
  onClose: () => void;
  actions: SpotlightAction[];
  placeholder?: string;
  gradientVariant?: GradientVariant;
  shortcut?: string;
}

const Spotlight = forwardRef<HTMLDivElement, SpotlightProps>(
  (
    {
      opened,
      onClose,
      actions,
      placeholder = 'Search...',
      gradientVariant = 'primary',
      shortcut = '⌘K',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const gradient = theme.other.gradients[gradientVariant];
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);

    const filteredActions = actions.filter(
      (action) =>
        action.label.toLowerCase().includes(query.toLowerCase()) ||
        action.description?.toLowerCase().includes(query.toLowerCase())
    );

    useEffect(() => {
      if (!opened) {
        setQuery('');
        setSelectedIndex(0);
      }
    }, [opened]);

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!opened) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % filteredActions.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + filteredActions.length) % filteredActions.length);
        } else if (e.key === 'Enter' && filteredActions[selectedIndex]) {
          e.preventDefault();
          filteredActions[selectedIndex].onClick();
          onClose();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [opened, selectedIndex, filteredActions, onClose]);

    return (
      <Transition mounted={opened} transition="pop" duration={200}>
        {(styles) => (
          <>
            {/* Backdrop */}
            <Box
              onClick={onClose}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                zIndex: 999,
                ...styles,
              }}
            />

            {/* Spotlight */}
            <Paper
              ref={ref}
              shadow="xl"
              radius="lg"
              style={{
                position: 'fixed',
                top: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: 600,
                zIndex: 1000,
                overflow: 'hidden',
                ...styles,
              }}
            >
              {/* Search input */}
              <Group p="md" style={{ borderBottom: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}` }}>
                <IconSearch size={20} style={{ color: gradient.from }} />
                <TextInput
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={placeholder}
                  variant="unstyled"
                  style={{ flex: 1 }}
                  autoFocus
                />
                <Text size="xs" c="dimmed">
                  {shortcut}
                </Text>
                <CloseButton onClick={onClose} />
              </Group>

              {/* Results */}
              <Stack gap={0} p="xs" mah={400} style={{ overflowY: 'auto' }}>
                {filteredActions.length === 0 ? (
                  <Text p="md" c="dimmed" ta="center">
                    No results found
                  </Text>
                ) : (
                  filteredActions.map((action, index) => (
                    <Box
                      key={action.id}
                      onClick={() => {
                        action.onClick();
                        onClose();
                      }}
                      p="sm"
                      style={{
                        borderRadius: theme.radius.md,
                        cursor: 'pointer',
                        background:
                          index === selectedIndex
                            ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
                            : 'transparent',
                        color: index === selectedIndex ? 'white' : undefined,
                      }}
                    >
                      <Group gap="sm">
                        {action.icon && (
                          <Box style={{ opacity: index === selectedIndex ? 1 : 0.7 }}>
                            {action.icon}
                          </Box>
                        )}
                        <Box>
                          <Text fw={500} size="sm">
                            {action.label}
                          </Text>
                          {action.description && (
                            <Text size="xs" opacity={0.8}>
                              {action.description}
                            </Text>
                          )}
                        </Box>
                      </Group>
                    </Box>
                  ))
                )}
              </Stack>
            </Paper>
          </>
        )}
      </Transition>
    );
  }
);

Spotlight.displayName = 'Spotlight';

export default Spotlight;
